<script lang="ts">
	import Letterhead from '$lib/components/Letterhead.svelte';
	import HipaaShield from '$lib/components/HipaaShield.svelte';
	import { canonicalUrl, jsonLdGraph, ogImageUrl, pageTitle } from '$lib/seo';
	import { trackTrustPacketDownload } from '$lib/analytics';

	let { data } = $props();
	const meta = $derived(data.seo!);

	function onPacket(kind: 'verification' | 'baa') {
		trackTrustPacketDownload(kind);
	}
</script>

<svelte:head>
	<title>{pageTitle(meta)}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={canonicalUrl(meta)} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content={canonicalUrl(meta)} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:image" content={ogImageUrl(meta)} />
	<meta name="twitter:card" content="summary_large_image" />
	{@html `<script type="application/ld+json">${jsonLdGraph(meta)}</script>`}
</svelte:head>

<article class="trust">
	<div class="container-wide">
		<Letterhead>
			<header class="trust-head">
				<h1 class="trust-title">On trust.</h1>
				<p class="trust-lede">
					Two questions matter on call one: what protects the patient's data, and what proves it.
					Below is the answer to both — and the artefacts your auditor and counsel will ask for.
				</p>
			</header>

			<!-- §01 HIPAA 2026 -->
			<section class="block" id="hipaa-2026">
				<p class="block-eyebrow">§01</p>
				<h2 class="block-head">Built for the 2026 HIPAA Security Rule.</h2>
				<div class="block-with-shield">
					<p>
						The May 2026 final rule converts every former "addressable" safeguard into a mandatory
						control: MFA, encryption at rest, encryption in transit, audit logging, network
						segmentation, penetration testing, and — most operationally — annual written
						verification that those controls are in place.
					</p>
					<HipaaShield size={88} />
				</div>
				<p>
					Ekavi delivers your verification packet on a fixed cadence: <em>every January 15</em>,
					signed and dated, sent to the practice manager and the compliance contact you nominate.
					The packet contains:
				</p>
				<ul class="block-list">
					<li>SOC 2 Type II report (current attestation)</li>
					<li>Encryption inventory (at rest, in transit, key custody)</li>
					<li>Access control policy and SSO posture</li>
					<li>Audit log retention proof and sample export</li>
					<li>Penetration test summary (current calendar year)</li>
					<li>Network segmentation diagram</li>
					<li>Sub-processor list with effective dates</li>
					<li>Incident response plan and most recent table-top result</li>
				</ul>
				<p class="block-cta">
					<a href="/hipaa/" onclick={() => onPacket('verification')}>
						Read how we cover HIPAA
						<span class="cta-arrow" aria-hidden="true">→</span>
					</a>
				</p>
			</section>

			<!-- §02 BAA -->
			<section class="block" id="baa">
				<p class="block-eyebrow">§02</p>
				<h2 class="block-head">A BAA written for AI.</h2>
				<p>
					Standard BAA templates were drafted before AI vendors existed. Ours is rewritten for the
					questions your counsel will actually ask:
				</p>
				<ul class="block-list">
					<li><strong>Training.</strong> PHI does not train our models. No exceptions.</li>
					<li>
						<strong>Retention.</strong> Ninety days default. Configurable to seven days on request.
					</li>
					<li>
						<strong>Sub-processors.</strong> Named, listed, and changeable with thirty days' written notice.
					</li>
					<li><strong>Breach notification.</strong> Twenty-four hours.</li>
					<li><strong>Termination.</strong> Clean offboarding with a 90-day data export window.</li>
					<li>
						<strong>Cure period.</strong> Thirty days for material breach. After, full export and deletion.
					</li>
				</ul>
				<p class="block-cta">
					<a href="mailto:trust@ekavi.care?subject=Sample%20BAA" onclick={() => onPacket('baa')}>
						Request the sample BAA
						<span class="cta-arrow" aria-hidden="true">→</span>
					</a>
				</p>
			</section>

			<!-- §03 Audit log -->
			<section class="block" id="audit-log">
				<p class="block-eyebrow">§03</p>
				<h2 class="block-head">Audit log on every action.</h2>
				<p>
					Every Ekavi action — read, draft, send, retry, decline — is recorded the moment it
					happens. Each row carries:
				</p>
				<ul class="block-list">
					<li>Action and outcome</li>
					<li>Operator (Ekavi or a named human) and confidence score</li>
					<li>Model and prompt fingerprint</li>
					<li>PHI fields touched</li>
					<li>Timestamp (UTC, with practice timezone offset)</li>
					<li>Trace ID linking to the source EHR event</li>
				</ul>
				<p>
					Retention: seven years. Export: CSV or signed PDF. Read access: every user, scoped by
					RBAC.
				</p>
				<pre class="log-sample">
2026-05-05T14:02:11Z  draft.appeal       conf=0.91  patient=hernandez_m  ehr=athena  trace=evt-9112334
2026-05-05T14:02:14Z  send.appeal        conf=0.91  payer=uhc           result=queued trace=evt-9112334
2026-05-05T14:48:22Z  recover.payer      conf=1.00  payer=uhc           result=approved auth=UHC-PA-9912034</pre>
			</section>

			<!-- §04 HTI-1 -->
			<section class="block" id="hti1">
				<p class="block-eyebrow">§04</p>
				<h2 class="block-head">HTI-1 algorithm transparency.</h2>
				<p>
					ONC's enforcement discretion ends March 1, 2026. Any certified health IT touching
					decisions that affect care must publish how its predictive tools work. Ekavi sits at that
					line — admin AI that affects care timing.
				</p>
				<p>What we publish:</p>
				<ul class="block-list">
					<li>One model card per task (PA drafting, denial appeal, eligibility, follow-up)</li>
					<li>The confidence threshold per task, with the failure modes we know about</li>
					<li>
						A reason citation on every drafted action ("Drafted from UHC denial CO-197 on May 1.")
					</li>
					<li>
						Quarterly performance report — accuracy, drift, escalation rate, denial recovery rate
					</li>
				</ul>
			</section>

			<!-- §05 Section 1557 -->
			<section class="block" id="section-1557">
				<p class="block-eyebrow">§05</p>
				<h2 class="block-head">Section 1557 — administrative-only.</h2>
				<p>
					Ekavi does not make clinical decisions. Patient care decisions remain with the physician.
					Our scope is administrative: prior auths, denials, eligibility, scheduling, follow-up.
				</p>
				<p>
					Practice-side artefacts are included with onboarding: a written AI-use policy, staff
					training templates, the audit log every Section 1557 audit asks for, and the patient
					disclosure copy you can paste into your forms or NPP.
				</p>
			</section>

			<!-- §06 Sub-processors -->
			<section class="block" id="sub-processors">
				<p class="block-eyebrow">§06</p>
				<h2 class="block-head">Hosting & sub-processors.</h2>
				<ul class="block-list">
					<li>
						<strong>Compute & storage.</strong> AWS us-east region, dedicated VPC, encrypted at rest with
						customer-managed keys.
					</li>
					<li>
						<strong>Database.</strong> Postgres + pgvector, HIPAA-eligible, encryption-at-rest with KMS.
					</li>
					<li>
						<strong>Model providers.</strong> Anthropic and OpenAI on the zero-retention API tier with
						BAA signed. PHI is redacted before any prompt leaves the VPC.
					</li>
					<li>
						<strong>Email.</strong> Resend (transactional only — application form replies). No PHI in
						transit.
					</li>
					<li>
						<strong>Sub-processor changes.</strong> Thirty days' written notice; the BAA permits termination
						if you object.
					</li>
				</ul>
			</section>

			<!-- §07 Incident response -->
			<section class="block" id="incident-response">
				<p class="block-eyebrow">§07</p>
				<h2 class="block-head">Incident response.</h2>
				<ul class="block-list">
					<li>Twenty-four hour breach notification (HIPAA-aligned).</li>
					<li>Seventy-two hour cyber-incident reporting (CIRCIA-aligned, expected June 2026).</li>
					<li>Public status page at <a href="https://status.ekavi.care">status.ekavi.care</a>.</li>
					<li>Post-incident review shared with affected practices within ten business days.</li>
				</ul>
			</section>

			<!-- §08 Offboarding -->
			<section class="block" id="offboarding">
				<p class="block-eyebrow">§08</p>
				<h2 class="block-head">Data export & offboarding.</h2>
				<p>
					You can leave at any time. The 90-day export window is automatic, no charge, and includes:
				</p>
				<ul class="block-list">
					<li>All PHI we processed, in CSV + JSONL</li>
					<li>The full audit log, in CSV + signed PDF</li>
					<li>All drafted documents (appeals, PAs, eligibility decisions) as PDF</li>
					<li>A written deletion certification with the cryptographic-erase timestamp</li>
				</ul>
			</section>

			<footer class="trust-foot">
				<hr class="trust-rule" />
				<p class="trust-meta">Last edited May 2026.</p>
			</footer>
		</Letterhead>
	</div>
</article>

<style>
	.trust {
		padding: var(--ekavi-space-12) 0 0;
	}
	.trust-head {
		margin-bottom: var(--ekavi-space-12);
	}
	/* HIPAA shield credential sits next to the first §01 paragraph,
	   vertically centered with it. Wraps below the paragraph on mobile. */
	.block-with-shield {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--ekavi-space-6);
		flex-wrap: wrap;
		margin: 0 0 var(--ekavi-space-4);
	}
	.block-with-shield p {
		flex: 1 1 auto;
		min-width: 0;
		margin: 0;
	}
	.trust-title {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-40);
		letter-spacing: var(--ekavi-track-headers);
		line-height: var(--ekavi-leading-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-6);
	}
	@media (max-width: 640px) {
		.trust-title {
			font-size: var(--ekavi-text-28);
		}
	}
	.trust-lede {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-20);
		color: var(--ekavi-ink-2);
		line-height: var(--ekavi-leading-body);
		max-width: 38ch;
		margin: 0;
	}

	.block {
		padding: var(--ekavi-space-9) 0;
		border-top: var(--ekavi-stroke-hairline);
	}
	.block:first-of-type {
		border-top: none;
	}
	.block-eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		margin: 0 0 var(--ekavi-space-2);
	}
	.block-head {
		font-family: var(--ekavi-font-ui);
		font-weight: var(--ekavi-weight-kraftig);
		font-size: var(--ekavi-text-28);
		letter-spacing: var(--ekavi-track-headers);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
		max-width: 32ch;
	}
	.block p {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: var(--ekavi-leading-body);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
		max-width: 56ch;
	}
	.block-list {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-16);
		line-height: var(--ekavi-leading-body);
		color: var(--ekavi-ink-1);
		margin: 0 0 var(--ekavi-space-4);
		padding-left: var(--ekavi-space-4);
		max-width: 56ch;
	}
	.block-list li {
		padding: var(--ekavi-space-2) 0;
		border-bottom: var(--ekavi-stroke-hairline);
		list-style: none;
	}
	.block-list li:last-child {
		border-bottom: none;
	}
	.block-list li strong {
		font-weight: var(--ekavi-weight-halbfett);
	}
	.block-cta {
		margin-top: var(--ekavi-space-4);
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
	}
	.log-sample {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-2);
		background: var(--ekavi-paper);
		padding: var(--ekavi-space-4);
		border-left: 1px solid var(--ekavi-charcoal);
		overflow-x: auto;
		line-height: 1.7;
		margin: var(--ekavi-space-4) 0;
	}

	.trust-foot {
		margin-top: var(--ekavi-space-12);
		padding-top: var(--ekavi-space-9);
	}
	.trust-rule {
		border: none;
		border-top: var(--ekavi-stroke-hairline);
		margin: 0 0 var(--ekavi-space-9);
	}
	.trust-meta {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
		margin: 0;
	}
</style>
